import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

const FlyingElements = ({ count = 'normal' }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Adjust counts based on prop
  const counts = {
    normal: { circles: 4, shapes: 3, particles: 6, lines: 2 },
    many: { circles: 6, shapes: 4, particles: 8, lines: 3 },
    few: { circles: 2, shapes: 2, particles: 4, lines: 1 },
  };
  
  const config = counts[count] || counts.normal;
  
  // Theme-aware colors
  const borderColor = isDark ? 'border-afsc-gold/20' : 'border-afsc-gold/40';
  const shapeBorderColor = isDark ? 'border-afsc-gold/30' : 'border-afsc-gold/50';
  const particleBg = isDark ? 'bg-afsc-gold/40' : 'bg-afsc-gold/60';
  const lineGradient = isDark ? 'via-afsc-gold/30' : 'via-afsc-gold/50';

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Floating circles */}
      {[...Array(config.circles)].map((_, i) => (
        <motion.div
          key={`circle-${i}`}
          className={`absolute rounded-full border ${borderColor}`}
          style={{
            width: `${20 + i * 15}px`,
            height: `${20 + i * 15}px`,
            left: `${10 + i * 15}%`,
            top: `${20 + (i % 3) * 25}%`,
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, 20, 0],
            opacity: isDark ? [0.2, 0.5, 0.2] : [0.3, 0.6, 0.3],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 3 + i * 0.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.3,
          }}
        />
      ))}

      {/* Floating geometric shapes */}
      {[...Array(config.shapes)].map((_, i) => (
        <motion.div
          key={`shape-${i}`}
          className="absolute"
          style={{
            width: `${30 + i * 10}px`,
            height: `${30 + i * 10}px`,
            right: `${15 + i * 20}%`,
            top: `${30 + (i % 2) * 40}%`,
          }}
          animate={{
            y: [0, -40, 0],
            x: [0, -25, 0],
            rotate: [0, 180, 360],
            opacity: isDark ? [0.15, 0.4, 0.15] : [0.25, 0.6, 0.25],
          }}
          transition={{
            duration: 4 + i * 0.7,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.5,
          }}
        >
          <div className={`w-full h-full border-2 ${shapeBorderColor} rotate-45`} />
        </motion.div>
      ))}

      {/* Floating particles */}
      {[...Array(config.particles)].map((_, i) => (
        <motion.div
          key={`particle-${i}`}
          className={`absolute w-2 h-2 ${particleBg} rounded-full`}
          style={{
            left: `${5 + (i % 4) * 25}%`,
            top: `${10 + Math.floor(i / 4) * 50}%`,
          }}
          animate={{
            y: [0, -50, 0],
            x: [0, Math.sin(i) * 30, 0],
            opacity: isDark ? [0, 0.6, 0] : [0, 0.8, 0],
            scale: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 2.5 + i * 0.3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.2,
          }}
        />
      ))}

      {/* Floating lines */}
      {[...Array(config.lines)].map((_, i) => (
        <motion.div
          key={`line-${i}`}
          className={`absolute h-px bg-gradient-to-r from-transparent ${lineGradient} to-transparent`}
          style={{
            width: `${100 + i * 50}px`,
            left: `${20 + i * 30}%`,
            top: `${25 + i * 30}%`,
          }}
          animate={{
            y: [0, -60, 0],
            x: [0, 40, 0],
            opacity: isDark ? [0, 0.5, 0] : [0, 0.7, 0],
            rotate: [0, 45, 0],
          }}
          transition={{
            duration: 5 + i,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.8,
          }}
        />
      ))}
    </div>
  );
};

export default FlyingElements;

